import { useRef, useState } from 'react'
import type { ParcelCard, RouterOutputs } from 'shared/api'
import { cashCancelResultKey, collectFailureKey, trpcClient } from 'shared/api'

/**
 * Taking the Delivery Fee at the counter — ticket 14, part of the handover
 * counter flow (spec §7). The operator takes cash — the counter takes nothing
 * else (ADR-0015) — and the system records a `collected`
 * Money Event; whether a Parcel is paid is FOLDED from those
 * events, never stored (ADR-0013/0015). This panel is the operator's view of
 * that fact: it shows whether the Parcel is paid and what was collected, and —
 * while the Parcel is on the shelf and not yet paid — lets the operator take the
 * fee.
 *
 * The collect call is the server's, and so is the amount: the panel shows the
 * Delivery Fee owed and sends none — the server takes the line's amount under the
 * partner's "does the Recipient pay" (ADR-0032) — then renders the folded result. The call is injected (`collect`) so it is the
 * stubbed tRPC seam in tests (spec Testing Decisions). Every operator-facing
 * string goes through the key-based i18n lookup; Russian is the only shipped
 * language (spec §12).
 *
 * Ticket 17 — a prepaid Parcel (the customer paid in the partner's app) folds as
 * paid with NO counter collection: the money never touched us, so `collected` is
 * null. The panel shows it as already paid and offers **no payment step** — the
 * fee must not be taken a second time (DoD). The gate is source-agnostic on the
 * server; the panel simply never offers collection once `paid?` is true.
 *
 * Release 2 ticket 14 — cancelling the cash is offered only when the server says
 * this Staff member may do it now (`cancelVerdict`, from the parcel detail): the
 * operator within the Shift the cash was taken in, if the Cashbox can give it
 * back; support after that Shift. Otherwise the panel names why not — a
 * handed-over Parcel has its handover cancelled first. Cancelling gives the cash
 * back, so it asks first (`AlertDialog`).
 */

export type PaymentView = ParcelCard['payment']
type CollectResult = RouterOutputs['payments']['Collect']
type CancelResult = RouterOutputs['payments']['CancelCollected']
type ParcelState = ParcelCard['state']
export type CancelVerdict = ParcelCard['corrections']['cancelCash']

async function collectViaTrpc(input: {
	parcelId: string
	idempotencyKey?: string
}): Promise<CollectResult> {
	return trpcClient.payments.Collect.mutate(input)
}

async function cancelViaTrpc(input: { parcelId: string; idempotencyKey: string }): Promise<CancelResult> {
	return trpcClient.payments.CancelCollected.mutate(input)
}

const newCommandKey = () =>
	globalThis.crypto?.randomUUID?.() ?? `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`

export interface PaymentPanelProps {
	parcelId: string
	/** The Parcel's current custody state — the fee is taken only while ON_SHELF. */
	state: ParcelState
	/** The folded payment view from the parcel detail (paid? and what was collected). */
	payment: PaymentView
	/** The Delivery Fee owed, shown; the server decides what is taken. */
	amountOwed: number
	/**
	 * Shows whether the Parcel is paid and what was collected, with no payment
	 * step and no cancellation — for a role that reads a Parcel but does not
	 * change it (a cashier, support; ADR-0029).
	 */
	readOnly?: boolean
	/** Whether the caller may cancel the cash now, and why not — the server's answer. */
	cancelVerdict: CancelVerdict
	/** Test seam: the collect call. Defaults to the real tRPC client. */
	collect?: (input: { parcelId: string; idempotencyKey?: string }) => Promise<CollectResult>
	cancelCollected?: (input: { parcelId: string; idempotencyKey: string }) => Promise<CancelResult>
	/** Called with the folded result after a successful collection. */
	onCollected?: (view: CollectResult) => void
	onCancelled?: (view: CancelResult) => void
}

/** The payment's state and its two acts, for the card to place where it wants them. */
export function usePayment({
	parcelId,
	state,
	payment,
	amountOwed,
	readOnly = false,
	cancelVerdict,
	collect = collectViaTrpc,
	cancelCollected = cancelViaTrpc,
	onCollected,
	onCancelled,
}: PaymentPanelProps) {
	const [busy, setBusy] = useState(false)
	// The i18n KEY of the current error, not its translated text, so the flag stays
	// truthy even when a message resolves to an empty string.
	const [errorKey, setErrorKey] = useState<string | null>(null)
	const [confirming, setConfirming] = useState(false)
	const collectKey = useRef(newCommandKey())
	const cancelKey = useRef(newCommandKey())

	// The fold is the source of truth for the panel: once a collection lands, the
	// panel shows the paid state. Local state mirrors the last folded result so the
	// panel updates in place without waiting for the detail query to refetch.
	const [view, setView] = useState<PaymentView>(payment)

	const submit = async () => {
		setBusy(true)
		setErrorKey(null)
		try {
			const result = await collect({
				parcelId,
				idempotencyKey: collectKey.current,
			})
			setView(result.payment)
			collectKey.current = newCommandKey()
			onCollected?.(result)
		} catch (error) {
			setErrorKey(collectFailureKey(error))
		} finally {
			setBusy(false)
		}
	}

	const cancel = async () => {
		setBusy(true)
		setErrorKey(null)
		try {
			const result = await cancelCollected({
				parcelId,
				idempotencyKey: cancelKey.current,
			})
			const refusal = cashCancelResultKey(result)
			if (refusal) {
				setErrorKey(refusal)
				return
			}
			if (result.outcome === 'cancelled' || result.outcome === 'already_cancelled') {
				setView(result.payment)
				cancelKey.current = newCommandKey()
				onCancelled?.(result)
			}
		} catch {
			setErrorKey('payment.cancelFailed')
		} finally {
			setBusy(false)
			setConfirming(false)
		}
	}

	/** Cash may be taken here now: unpaid, something to pay, on the shelf, a role that takes it. */
	const collectable = !view.paid && amountOwed > 0 && !readOnly && state === 'ON_SHELF'
	/** The cash may be cancelled now — the server's answer. */
	const cancellable = !!(view.paid && view.collected && cancelVerdict.allowed)
	/** Why the cash may not be cancelled, when it is worth saying (not to a role that never cancels). */
	const cancelUnavailable =
		view.paid &&
		view.collected &&
		!cancelVerdict.allowed &&
		cancelVerdict.reason &&
		cancelVerdict.reason !== 'not_permitted' &&
		cancelVerdict.reason !== 'nothing_to_cancel'
			? cancelVerdict.reason
			: null

	return {
		view,
		amountOwed,
		state,
		readOnly,
		busy,
		errorKey,
		collectable,
		cancellable,
		cancelUnavailable,
		confirming,
		askCancel: () => setConfirming(true),
		setConfirming,
		submit,
		cancel,
	}
}

export type PaymentModel = ReturnType<typeof usePayment>
